import React from 'react';
import footerContent from 'platform/forms/components/FormFooter';
import { CONTACTS } from '@department-of-veterans-affairs/component-library/contacts';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import { VA_FORM_IDS } from 'platform/forms/constants';
import { profilePersonalInfoPage } from 'platform/forms-system/src/js/patterns/prefill/PersonalInformation';
import { profileContactInfoPages } from 'platform/forms-system/src/js/patterns/prefill/ContactInfo';
import { getContent } from 'platform/forms-system/src/js/utilities/data/profile';
import commonDefinitions from 'vets-json-schema/dist/definitions.json';
import { TITLE, SUBTITLE, SUBMIT_URL } from '../constants';
import { organizationRepresentativesArrayOptions } from '../helpers';

import manifest from '../manifest.json';
import transform from './transform';
import submitForm from './submitForm';

import IntroductionPage from '../containers/IntroductionPage';
import ConfirmationPage from '../containers/ConfirmationPage';
import PrivacyPolicy from '../components/PrivacyPolicy';
import PreSubmitInfo from '../components/PreSubmitInfo';
import CustomReviewTopContent from '../components/CustomReviewTopContent';
import thirdPartyOrganizationRepresentativesSummary from '../pages/thirdPartyOrganizationRepresentativesSummary ';
import thirdPartyOrganizationInformation from '../pages/thirdPartyOrganizationInformation';
import thirdPartyOrganizationRepresentativesIntro from '../pages/thirdPartyOrganizationRepresentativesIntro';
import thirdPartyOrganizationRepresentativeName from '../pages/thirdPartyOrganizationRepresentativesName';
import informationToDisclose from '../pages/informationToDisclose';
import prefillTransform from './prefillTransform';

import {
  thirdPartyPersonName,
  thirdPartyPersonAddress,
  discloseInformation,
  informationToDiscloseOther,
  lengthOfRelease,
  securitySetup,
  securitySetupPinPassword,
  securitySetupCustomQuestion,
  securitySetupMotherBornLocation,
  securitySetupHighSchool,
  securitySetupPetName,
  securitySetupTeacherName,
  securitySetupFatherMiddleName,
} from '../pages';

const { fullName, ssn, date, dateRange, usaPhone } = commonDefinitions;

/** @type {FormConfig} */
const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  submitUrl: SUBMIT_URL,
  submit: submitForm,
  trackingPrefix: 'edu-10278-',
  introduction: IntroductionPage,
  confirmation: ConfirmationPage,
  formId: VA_FORM_IDS.FORM_22_10278,
  useCustomScrollAndFocus: true,
  saveInProgress: {
    messages: {
      inProgress: 'Your form (22-10278) is in progress.',
      expired:
        'Your saved form (22-10278) has expired. Please start a new form.',
      saved: 'Your form has been saved.',
    },
  },
  version: 0,
  prefillEnabled: true,
  prefillTransformer: prefillTransform,
  preSubmitInfo: {
    CustomComponent: PreSubmitInfo,
    statementOfTruth: {
      heading: 'Declaration of intent',
      body: PrivacyPolicy,
      messageAriaDescribedby: 'I have read and accept the privacy policy.',
      fullNamePath: 'claimantPersonalInformation.fullName',
    },
  },
  savedFormMessages: {
    notFound: 'Please start over.',
    noAuth: 'Please sign in again to continue your form.',
  },
  title: TITLE,
  subTitle: SUBTITLE,
  CustomReviewTopContent,
  customText: {
    appType: 'form',
    continueAppButtonText: 'Continue your form',
    startNewAppButtonText: 'Start a new form',
    finishAppLaterMessage: 'Finish this form later',
    appSavedSuccessfullyMessage: 'We’ve saved your form.',
    reviewPageTitle: 'Review',
    submitButtonText: 'Submit',
  },
  defaultDefinitions: {
    fullName,
    ssn,
    date,
    dateRange,
    usaPhone,
  },
  transformForSubmit: transform,
  chapters: {
    personalInformationChapter: {
      title: 'Your personal information',
      pages: {
        ...profilePersonalInfoPage({
          personalInfoConfig: {
            name: { show: true, required: true },
            ssn: { show: true, required: true },
            dateOfBirth: { show: true, required: true },
          },
          dataAdapter: {
            ssnPath: 'ssn',
          },
          depends: formData => formData?.userLoggedIn === true,
          note: (
            <div className="vads-u-margin-bottom--4">
              <p>
                <strong>Note:</strong> To protect your personal information, we
                don’t allow online changes to your name, date of birth, or
                Social Security number. If you need to change this information,
                call us at <va-telephone contact={CONTACTS.VA_BENEFITS} /> (
                <va-telephone contact="711" tty />
                ). We’re here Monday through Friday, between 8:00 a.m. and 9:00
                p.m. ET.
              </p>
              <va-link
                external
                href="https://www.va.gov/resources/how-to-change-your-legal-name-on-file-with-va/"
                text="Find more detailed instructions for how to change your legal name"
              />
            </div>
          ),
        }),
        ...profileContactInfoPages({
          content: {
            ...getContent('request'),
            title: 'Confirm the contact information we have on file for you',
            description: null,
          },
          prefillPatternEnabled: false,
        }),
      },
    },
    disclosureChapter: {
      title: 'Type of third party',
      pages: {
        discloseInformation: {
          path: 'type-of-third-party',
          title: 'Disclose your personal information to a third party',
          uiSchema: discloseInformation.uiSchema,
          schema: discloseInformation.schema,
          onNavForward: ({ formData, goPath }) => {
            if (formData?.discloseInformation?.authorize === 'organization') {
              goPath('/organization-name-and-address');
              return;
            }
            goPath('/third-party-person-details');
          },
        },
      },
    },
    thirdPartyContactInformation: {
      title: 'Third party contact information',
      pages: {
        thirdPartyPersonName: {
          path: 'third-party-person-details',
          title: 'Name of person',
          uiSchema: thirdPartyPersonName.uiSchema,
          schema: thirdPartyPersonName.schema,
          depends: formData =>
            formData?.discloseInformation?.authorize === 'person',
        },
        thirdPartyPersonAddress: {
          path: 'third-party-person-details-1',
          title: 'Address of person',
          uiSchema: thirdPartyPersonAddress.uiSchema,
          schema: thirdPartyPersonAddress.schema,
          depends: formData =>
            formData?.discloseInformation?.authorize === 'person',
        },
      },
    },
    thirdPartyOrganizationsChapter: {
      title: 'Third party contact information',
      pages: {
        thirdPartyOrganizationInformation: {
          title: 'Organization name and address',
          path: 'organization-name-and-address',
          uiSchema: thirdPartyOrganizationInformation.uiSchema,
          schema: thirdPartyOrganizationInformation.schema,
          depends: formData =>
            formData?.discloseInformation?.authorize === 'organization',
        },
        ...arrayBuilderPages(
          organizationRepresentativesArrayOptions,
          pageBuilder => ({
            thirdPartyOrganizationRepresentativesIntro: pageBuilder.introPage({
              title: 'Name of organization’s representatives',
              path: 'organizations/representatives',
              uiSchema: thirdPartyOrganizationRepresentativesIntro.uiSchema,
              schema: thirdPartyOrganizationRepresentativesIntro.schema,
              depends: formData =>
                formData?.discloseInformation?.authorize === 'organization',
            }),
            thirdPartyOrganizationRepresentativesSummary:
              pageBuilder.summaryPage({
                title: 'Review representatives',
                path: 'organizations/representatives-summary',
                uiSchema: thirdPartyOrganizationRepresentativesSummary.uiSchema,
                schema: thirdPartyOrganizationRepresentativesSummary.schema,
                depends: formData =>
                  formData?.discloseInformation?.authorize === 'organization',
              }),
            thirdPartyOrganizationRepresentativeName: pageBuilder.itemPage({
              title: 'Name of organization’s representatives',
              path: 'organizations/representatives/:index',
              showPagePerItem: true,
              uiSchema: thirdPartyOrganizationRepresentativeName.uiSchema,
              schema: thirdPartyOrganizationRepresentativeName.schema,
              depends: formData =>
                formData?.discloseInformation?.authorize === 'organization',
            }),
          }),
        ),
      },
    },
    informationToDiscloseChapter: {
      title: 'Information to disclose',
      pages: {
        informationToDisclose: {
          path: 'information-to-disclose',
          title: 'Information to disclose',
          uiSchema: informationToDisclose.uiSchema,
          schema: informationToDisclose.schema,
          onNavBack: ({ formData, goPath }) => {
            if (formData?.discloseInformation?.authorize === 'organization') {
              goPath('/organizations/representatives-summary');
              return;
            }
            goPath('/third-party-person-details-1');
          },
        },
        informationToDiscloseOther: {
          path: 'information-to-disclose-other',
          title: 'Other information to disclose',
          uiSchema: informationToDiscloseOther.uiSchema,
          schema: informationToDiscloseOther.schema,
        },
      },
    },
    lengthOfReleaseChapter: {
      title: 'Length of release',
      pages: {
        lengthOfRelease: {
          path: 'length-of-release',
          title: 'Length of release',
          uiSchema: lengthOfRelease.uiSchema,
          schema: lengthOfRelease.schema,
        },
      },
    },
    securitySetupChapter: {
      title: 'Security setup',
      pages: {
        securitySetup: {
          path: 'security-setup',
          title: 'Security setup',
          uiSchema: securitySetup.uiSchema,
          schema: securitySetup.schema,
          updateFormData: securitySetup.updateFormData,
          onNavForward: ({ formData, goPath }) => {
            const question = formData?.securityQuestion?.question;
            if (question === 'pin') {
              goPath('/security-setup-pin-password');
            } else if (question === 'create') {
              goPath('/security-setup-custom-question');
            } else if (question === 'motherBornLocation') {
              goPath('/security-setup-mother-born-location');
            } else if (question === 'highSchool') {
              goPath('/security-setup-high-school');
            } else if (question === 'petName') {
              goPath('/security-setup-pet-name');
            } else if (question === 'teacherName') {
              goPath('/security-setup-teacher-name');
            } else if (question === 'fatherMiddleName') {
              goPath('/security-setup-father-middle-name');
            }
          },
        },
        securitySetupPinPassword: {
          path: 'security-setup-pin-password',
          title: 'Security setup',
          uiSchema: securitySetupPinPassword.uiSchema,
          schema: securitySetupPinPassword.schema,
          depends: formData => formData?.securityQuestion?.question === 'pin',
          onNavBack: ({ goPath }) => {
            goPath('/security-setup');
          },
        },
        securitySetupCustomQuestion: {
          path: 'security-setup-custom-question',
          title: 'Security setup',
          uiSchema: securitySetupCustomQuestion.uiSchema,
          schema: securitySetupCustomQuestion.schema,
          depends: formData =>
            formData?.securityQuestion?.question === 'create',
          onNavBack: ({ goPath }) => {
            goPath('/security-setup');
          },
        },
        securitySetupMotherBornLocation: {
          path: 'security-setup-mother-born-location',
          title: 'Security setup',
          uiSchema: securitySetupMotherBornLocation.uiSchema,
          schema: securitySetupMotherBornLocation.schema,
          depends: formData =>
            formData?.securityQuestion?.question === 'motherBornLocation',
          onNavBack: ({ goPath }) => {
            goPath('/security-setup');
          },
        },
        securitySetupHighSchool: {
          path: 'security-setup-high-school',
          title: 'Security setup',
          uiSchema: securitySetupHighSchool.uiSchema,
          schema: securitySetupHighSchool.schema,
          depends: formData =>
            formData?.securityQuestion?.question === 'highSchool',
          onNavBack: ({ goPath }) => {
            goPath('/security-setup');
          },
        },
        securitySetupPetName: {
          path: 'security-setup-pet-name',
          title: 'Security setup',
          uiSchema: securitySetupPetName.uiSchema,
          schema: securitySetupPetName.schema,
          depends: formData =>
            formData?.securityQuestion?.question === 'petName',
          onNavBack: ({ goPath }) => {
            goPath('/security-setup');
          },
        },
        securitySetupTeacherName: {
          path: 'security-setup-teacher-name',
          title: 'Security setup',
          uiSchema: securitySetupTeacherName.uiSchema,
          schema: securitySetupTeacherName.schema,
          depends: formData =>
            formData?.securityQuestion?.question === 'teacherName',
          onNavBack: ({ goPath }) => {
            goPath('/security-setup');
          },
        },
        securitySetupFatherMiddleName: {
          path: 'security-setup-father-middle-name',
          title: 'Security setup',
          uiSchema: securitySetupFatherMiddleName.uiSchema,
          schema: securitySetupFatherMiddleName.schema,
          depends: formData =>
            formData?.securityQuestion?.question === 'fatherMiddleName',
          onNavBack: ({ goPath }) => {
            goPath('/security-setup');
          },
        },
      },
    },
  },
  footerContent,
};

export default formConfig;
